import React, {KeyboardEvent} from 'react';
import {OperatorBranding, OperatorBrandingData} from "types/Operator";
import {Field, Form, Formik, FormikHandlers, FormikHelpers} from "formik";
import FormControls from "components/FormControls";
import Selector from "components/Form/Selector";
import Uploader from "components/Form/Uploader";
import FieldHeader from "components/Form/FieldHeader";

// Types ----

type BrandingFormProps = {
  settings?: OperatorBranding | null
  onSubmit: (values: OperatorBrandingData, helpers: FormikHelpers<OperatorBrandingData>) => any
}

// Helpers ----

const fonts = [
  {id: 'Raleway', name: 'Raleway'},
  {id: 'Lora', name: 'Lora'},
  {id: 'Roboto Mono', name: 'Roboto Mono'},
  {id: 'Grandstander', name: 'Grandstander'},
]

// Component ----

function BrandingForm({onSubmit, settings}: BrandingFormProps) {
  const initialValues: OperatorBrandingData = {
    logo_id: settings?.logo?.id || null,
    qr_logo_id: settings?.qr_logo?.id || null,
    favicon_id: settings?.favicon?.id || null,
    font: settings?.font || fonts[0].name,
    colour_1: settings?.colour_1 || '#000000',
    colour_2: settings?.colour_2 || '#000000',
    colour_3: settings?.colour_3 || '#000000',
    colour_4: settings?.colour_4 || '#000000',
  }

  function handleKeyDown(event: KeyboardEvent<HTMLFormElement>, {handleSubmit}: FormikHandlers) {
    if (event.ctrlKey && event.key === 'Enter') {
      handleSubmit();
    }
  }

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {formik => (
        <section aria-labelledby="branding">
          <Form onKeyDown={event => handleKeyDown(event, formik)}>
            <div className="shadow sm:rounded-md">
              <div className="bg-white py-6 px-4 sm:p-6 sm:rounded-md">
                <div>
                  <h2 id="branding" className="text-lg leading-6 font-medium text-gray-900">
                    Branding
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Set your logo, font and colour scheme
                  </p>
                </div>

                <div className="mt-6 grid grid-cols-6 gap-6">

                  {/* Logo */}
                  <div className="col-span-6">
                    <FieldHeader label="Logo"/>
                    <Uploader
                      id={'logo_id'}
                      name={'logo_id'}
                      type={Uploader.types.IMAGE}
                      maxFiles={1}
                      files={settings?.logo || null}
                      onChange={files => formik.setFieldValue('logo_id', files[0]?.id || null)}
                    />
                  </div>

                  {/* QR Logo */}
                  <div className="col-span-6">
                    <FieldHeader label="QR Logo"/>
                    <Uploader
                      id={'qr_logo_id'}
                      name={'qr_logo_id'}
                      type={Uploader.types.IMAGE}
                      maxFiles={1}
                      files={settings?.qr_logo || null}
                      onChange={files => formik.setFieldValue('qr_logo_id', files[0]?.id || null)}
                    />
                  </div>

                  {/* Favicon */}
                  <div className="col-span-6">
                    <FieldHeader label="Favicon"/>
                    <Uploader
                      id={'favicon_id'}
                      name={'favicon_id'}
                      type={Uploader.types.IMAGE}
                      maxFiles={1}
                      files={settings?.favicon || null}
                      onChange={files => formik.setFieldValue('favicon_id', files[0]?.id || null)}
                    />
                  </div>

                  {/* Font */}
                  <div className="col-span-4 sm:col-span-2 mb-2">
                    <Selector
                      label="Font"
                      items={fonts}
                      selectedId={settings?.font}
                      onChange={item => formik.setFieldValue('font', item.id)}
                    />
                  </div>

                  {/* TODO: Make color picker field into a component */}
                  {/* Colour 1 */}
                  <div className="col-span-2 col-start-1 sm:col-span-1 sm:col-start-1">
                    <FieldHeader label="Primary Colour"/>
                    <Field
                      type="color"
                      name="colour_1"
                      id="colour_1"
                      required
                      className="appearance-none w-full h-14 -ml-0.5 bg-transparent border-0 cursor-pointer"
                    />
                  </div>

                  {/* Colour 2 */}
                  <div className="col-span-2 sm:col-span-1">
                    <FieldHeader label="Secondary Colour"/>
                    <Field
                      type="color"
                      name="colour_2"
                      id="colour_2"
                      required
                      className="appearance-none w-full h-14 -ml-0.5 bg-transparent border-0 cursor-pointer"
                    />
                  </div>

                  {/* Colour 3 */}
                  <div className="col-span-2 col-start-1 sm:col-span-1">
                    <FieldHeader label="Third Colour"/>
                    <Field
                      type="color"
                      name="colour_3"
                      id="colour_3"
                      required
                      className="appearance-none w-full h-14 -ml-0.5 bg-transparent border-0 cursor-pointer"
                    />
                  </div>

                  {/* Colour 4 */}
                  <div className="col-span-2 sm:col-span-1">
                    <FieldHeader label="Fourth Colour"/>
                    <Field
                      type="color"
                      name="colour_4"
                      id="colour_4"
                      required
                      className="appearance-none w-full h-14 -ml-0.5 bg-transparent border-0 cursor-pointer"
                    />
                  </div>
                </div>
              </div>

              <FormControls formik={formik} compact withoutCancel/>
            </div>
          </Form>
        </section>
      )}
    </Formik>
  );
}

export default BrandingForm;